import { useState, useEffect, useRef, useId } from 'react';
import { supabase } from '../../lib/supabase';
import { getAppUrl } from '../../lib/url';
import { X, Loader2, Mail, Fingerprint, Eye, EyeOff, CheckCircle2, Circle } from 'lucide-react';
import { isAcceptablePassword, describePasswordRule, MIN_PASSWORD_LENGTH } from '../../lib/password';
import { errorMessage, reportError } from '../../lib/errors';
import './auth.css';

// ForgeDash identity (2026-10): midnight, graphite, ivory, silver and gold —
// see auth.css. The auth logic below is unchanged by the redesign: same
// Supabase calls, same validation, same redirects, same invite handling.

export type AuthView = 'signin' | 'signup';

interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
    defaultView?: AuthView;
    /**
     * When false this renders inline, as the content of a screen (LoginScreen
     * wraps it in AuthShell). When true it renders as a dismissible sheet over
     * the page, with a close button and Escape to close.
     */
    dismissible?: boolean;
    /** Called when the visitor moves between Sign in and Create account. */
    onViewChange?: (view: AuthView) => void;
}

/** Where to manage passkeys, named exactly as the app labels it. */
const PASSKEY_SETTINGS = 'Settings → Security & Login';

function GoogleIcon() {
    return (
        <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
    );
}

export function AuthModal({ isOpen, onClose, defaultView = 'signin', dismissible = true, onViewChange }: AuthModalProps) {
    const [view, setViewState] = useState<AuthView | 'forgot'>(defaultView);
    const [signupSuccess, setSignupSuccess] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState('');
    const [resetSent, setResetSent] = useState(false);

    // WebAuthn availability. Checked once rather than per render: it cannot
    // change during a page life, and a passkey button that always errors on an
    // unsupported browser is worse than no button at all.
    const passkeysSupported = typeof window !== 'undefined'
        && typeof window.PublicKeyCredential !== 'undefined';

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [legalAccepted, setLegalAccepted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Guidance that is not a failure — e.g. the passkey prompt was closed.
    const [notice, setNotice] = useState<string | null>(null);

    // Invite Logic (preserved — invited users skip workshop creation)
    const [inviteOrgName, setInviteOrgName] = useState<string | null>(null);
    const [inviteEmail, setInviteEmail] = useState<string | null>(null);
    const [inviteRole, setInviteRole] = useState<string | null>(null);

    // Focus goes to the new heading when the view changes from a link, so a
    // screen reader announces where the visitor now is.
    const titleRef = useRef<HTMLHeadingElement>(null);
    const focusTitleNext = useRef(false);
    const ids = useId();

    const setView = (next: AuthView | 'forgot', opts: { focusTitle?: boolean } = {}) => {
        setViewState(next);
        setError(null);
        setNotice(null);
        setShowPassword(false);
        focusTitleNext.current = !!opts.focusTitle;
        if (next !== 'forgot') onViewChange?.(next);
    };

    useEffect(() => {
        if (isOpen) {
            setViewState(defaultView);
            setSignupSuccess(false);
            setRegisteredEmail('');
            setResetSent(false);
        }
    }, [isOpen, defaultView]);

    useEffect(() => {
        if (focusTitleNext.current) {
            focusTitleNext.current = false;
            titleRef.current?.focus();
        }
    });

    useEffect(() => {
        const checkInvite = async () => {
            if (!isOpen) return;
            const params = new URLSearchParams(window.location.search);
            const token = params.get('invite_token') || localStorage.getItem('pending_invite');
            if (!token) return;

            let cleanToken = token;
            const uuidMatch = token.match(/[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}/i);
            if (uuidMatch) cleanToken = uuidMatch[0];
            const hexMatch = token.match(/[0-9a-f]{32}/i);
            if (!uuidMatch && hexMatch) cleanToken = hexMatch[0];

            // get_invite_details returns a JSON envelope that the generated
            // Supabase types render as `Json`, so every field access was silenced
            // with `@ts-ignore` — four of them, each also hiding any OTHER error
            // on its line, which is exactly what @ts-ignore is criticised for.
            //
            // Declaring the shape once says the same thing honestly: this is what
            // the RPC returns, and it is unchecked because it crosses a boundary
            // TypeScript cannot see through. The optional fields are optional
            // because an invite can carry no email and no pre-set role.
            interface InviteDetails {
                valid?: boolean;
                org_name?: string;
                email?: string;
                role?: string;
            }
            const { data, error: fetchError } = await supabase.rpc('get_invite_details', { token_text: cleanToken });
            if (fetchError) reportError(fetchError, 'auth-modal.get-invite-details');
            const invite = data as InviteDetails | null;

            if (invite?.valid) {
                if (invite.org_name) setInviteOrgName(invite.org_name);
                if (invite.email) { setInviteEmail(invite.email); setEmail(invite.email); }
                if (invite.role) setInviteRole(invite.role);
                setViewState('signup');
            }
        };
        checkInvite();
    }, [isOpen]);

    // Escape closes the overlay form (never the full-screen login).
    useEffect(() => {
        if (!isOpen || !dismissible) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [isOpen, dismissible, onClose]);

    if (!isOpen) return null;

    // Identity only. Business name / trade are collected AFTER signup in the
    // themed onboarding step (Option B). Signup never asks for them.
    //
    // Sign-IN only needs a non-empty password — the rule applies to passwords
    // being SET, and an existing account may predate the current minimum.
    // Enforcing 10 here would lock out anyone who signed up under the old rule.
    const passwordRuleMet = isAcceptablePassword(password.trim());
    const isFormValid = email.trim().includes('@') && (
        view === 'signup'
            ? passwordRuleMet && legalAccepted
            : password.trim().length > 0
    );

    const handleAuth = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setNotice(null);
        try {
            if (view === 'signup') {
                if (!legalAccepted) throw new Error('Please accept the terms and DPA before creating an account.');
                const { data, error } = await supabase.auth.signUp({ email, password, options: {
                    data: { legal_accepted: true, terms_version: '2026-09', dpa_version: '1.4', privacy_version: '2026-10-05' }
                } });
                if (error) throw error;
                if (data.user && !data.session) {
                    setRegisteredEmail(email);
                    setSignupSuccess(true);
                    focusTitleNext.current = true;
                    return;
                }
                onClose();
            } else {
                const { error } = await supabase.auth.signInWithPassword({ email, password });
                if (error) throw error;
                onClose();
            }
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // ------------------------------------------------------------------
    // Forgot password
    // ------------------------------------------------------------------
    // Deliberately reports success whether or not the address has an account.
    // Saying "no account with that email" turns this box into a free tool for
    // checking which of your customers' addresses are registered, and the user
    // who genuinely mistyped is served just as well by "if that address has an
    // account, a link is on its way".
    const handleForgotPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
                redirectTo: `${getAppUrl()}/reset-password`,
            });
            // A rate-limit refusal is worth showing; anything else is swallowed
            // for the enumeration reason above.
            if (error && error.status === 429) throw error;
            setResetSent(true);
            focusTitleNext.current = true;
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    // ------------------------------------------------------------------
    // Passkey sign-in
    // ------------------------------------------------------------------
    // No email needed up front — the authenticator already knows which
    // credential belongs to this site, so the browser presents the account
    // picker itself. Phishing-resistant by construction: a passkey is bound to
    // the origin and cannot be handed to a lookalike domain.
    //
    // Shown only when the browser can actually do WebAuthn, because a button
    // that always errors is worse than no button.
    //
    // What the messages can and cannot say. WebAuthn deliberately does not tell
    // a site whether the device holds a passkey for it: "no passkey here" and
    // "the visitor closed the prompt" both arrive as NotAllowedError. So that
    // case gets guidance, phrased as an "if" — never a claim about the account.
    // Only when the device DID offer a passkey and the server rejected it do we
    // know the credential is not registered (deleted, or made for another
    // account), and only then do we say so. No email is involved in either
    // path, so nothing here reveals whether an address has an account.
    const handlePasskeyLogin = async () => {
        setLoading(true);
        setError(null);
        setNotice(null);
        try {
            const { error } = await supabase.auth.signInWithPasskey();
            if (error) throw error;
            onClose();
        } catch (err) {
            const name = (err as { name?: string })?.name;
            const e = err as { code?: string; message?: string; status?: number } | null;
            // A project without the passkey feature enabled does not answer
            // `passkey_disabled` — /auth/v1/passkeys/* simply 404s, because the
            // route is not mounted. Same cause, same message: anything else
            // renders a bare "Not Found" that means nothing to the user.
            const disabled = e?.code === 'passkey_disabled'
                || e?.status === 404
                || /passkey.*disabled/i.test(e?.message ?? '');
            // The browser ceremony succeeded but the server does not recognise
            // the credential that was offered.
            const notRegistered = e?.code === 'invalid_credentials'
                || e?.code === 'identity_not_found'
                || e?.code === 'user_not_found'
                || /(passkey|credential|webauthn).*(not found|unknown|not registered|does not exist)/i.test(e?.message ?? '');

            if (name === 'NotAllowedError' || name === 'AbortError') {
                // Closing the OS prompt is a normal thing to do, not a failure
                // worth shouting about — but it is also what an unregistered
                // device looks like, so say what to do in that case.
                setNotice(`No passkey was used. If you haven't set one up for ForgeDash yet, sign in with your password or Google, then open ${PASSKEY_SETTINGS} to add one.`);
            } else if (disabled) {
                // "Passkeys are disabled" is GoTrue telling us about ITS
                // configuration. Repeating it verbatim reads as an accusation
                // that the user turned something off, and gives them nothing to
                // act on. Point at what does work.
                setError('Fingerprint sign-in is not switched on for this workspace yet. Use your password or Google for now — the button stays available so you can retry.');
            } else if (notRegistered) {
                setError(`Passkey sign-in isn't set up for this account. Sign in using your password or Google, then open ${PASSKEY_SETTINGS} to register a passkey.`);
            } else {
                setError(errorMessage(err));
            }
        } finally {
            setLoading(false);
        }
    };

    // Google needs a full-page redirect (it refuses to run in a frame), and
    // the session comes back to this origin through the existing callback.
    const handleGoogleLogin = async () => {
        setLoading(true);
        setError(null);
        setNotice(null);
        try {
            const pendingInvite = localStorage.getItem('pending_invite');
            const redirectUrl = pendingInvite
                ? `${window.location.origin}/?invite_token=${pendingInvite}`
                : getAppUrl();
            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: redirectUrl,
                    queryParams: { access_type: 'offline', prompt: 'select_account' },
                },
            });
            if (error) throw error;
        } catch (err) {
            setError(errorMessage(err));
            setLoading(false);
        }
    };

    const emailId = `${ids}-email`;
    const passwordId = `${ids}-password`;
    const ruleId = `${ids}-rule`;
    const errorId = `${ids}-error`;

    const errorBox = error && (
        <p id={errorId} className="fda-msg fda-msg--error" role="alert">{error}</p>
    );

    let body: React.ReactNode;

    if (signupSuccess) {
        // ── Email-confirmation sent ──────────────────────────────────────────
        body = (
            <div className="fda-view fda-done" key="verify">
                <div className="fda-done__icon" aria-hidden="true"><Mail /></div>
                <h1 className="fda-title" ref={titleRef} tabIndex={-1}>Check your inbox</h1>
                <p className="fda-sub">We&apos;ve sent a verification link to</p>
                <span className="fda-email">{registeredEmail}</span>
                <p className="fda-sub">
                    Open the link to confirm your account, then you&apos;ll set up your workshop. Check spam if it
                    hasn&apos;t arrived within a minute.
                </p>
                <button type="button" className="fda-btn fda-btn--passkey"
                    onClick={() => { setSignupSuccess(false); setView('signin', { focusTitle: true }); }}>
                    Back to sign in
                </button>
            </div>
        );
    } else if (view === 'forgot') {
        // ── Forgot password ──────────────────────────────────────────────────
        body = resetSent ? (
            <div className="fda-view fda-done" key="reset-sent">
                <div className="fda-done__icon" aria-hidden="true"><Mail /></div>
                <h1 className="fda-title" ref={titleRef} tabIndex={-1}>Check your inbox</h1>
                <p className="fda-sub">
                    If <b>{email.trim()}</b> has an account, a password reset link is on its way. It expires after a
                    short while, and can only be used once.
                </p>
                <button type="button" className="fda-btn fda-btn--primary"
                    onClick={() => { setResetSent(false); setView('signin', { focusTitle: true }); }}>
                    Back to sign in
                </button>
            </div>
        ) : (
            <div className="fda-view" key="forgot">
                <div className="fda-head">
                    <p className="fda-kicker">Password reset</p>
                    <h1 className="fda-title" ref={titleRef} tabIndex={-1}>Reset your password</h1>
                    <p className="fda-sub">Enter the email you sign in with and we&apos;ll send you a link.</p>
                </div>
                <form onSubmit={handleForgotPassword} className="fda-form">
                    {errorBox}
                    <div className="fda-field">
                        <label className="fda-label" htmlFor={emailId}>Email</label>
                        <input id={emailId} type="email" required autoFocus autoComplete="email" inputMode="email"
                            className="fda-input" placeholder="you@yourgarage.co.uk"
                            value={email} onChange={(e) => setEmail(e.target.value)}
                            aria-describedby={error ? errorId : undefined} />
                    </div>
                    <button type="submit" className="fda-btn fda-btn--primary" disabled={loading || !email.trim().includes('@')}>
                        {loading ? <><Loader2 className="fda-spin" aria-hidden="true" /> Sending…</> : 'Send reset link'}
                    </button>
                </form>
                <p className="fda-alt">
                    Remembered it?{' '}
                    <button type="button" className="fda-link" onClick={() => setView('signin', { focusTitle: true })}>
                        Back to sign in
                    </button>
                </p>
            </div>
        );
    } else {
        // ── Sign in / Create account ─────────────────────────────────────────
        const signup = view === 'signup';
        body = (
            <>
                {!inviteOrgName && (
                    <div className="fda-switch" role="group" aria-label="Choose how to continue">
                        {(['signup', 'signin'] as const).map(v => (
                            <button key={v} type="button" aria-pressed={view === v}
                                onClick={() => { if (view !== v) setView(v); }}>
                                {v === 'signup' ? 'Create account' : 'Sign in'}
                            </button>
                        ))}
                    </div>
                )}

                <div className="fda-view" key={view}>
                    <div className="fda-head">
                        <p className="fda-kicker">{inviteOrgName ? 'Team invite' : signup ? 'Start free' : 'Welcome back'}</p>
                        <h1 className="fda-title" ref={titleRef} tabIndex={-1}>
                            {inviteOrgName ? 'Join the team' : signup ? 'Create your account' : 'Sign in to ForgeDash'}
                        </h1>
                        <p className="fda-sub">
                            {inviteOrgName
                                ? `You've been invited to ${inviteOrgName}.`
                                : signup
                                    ? "Just your email and a password — you'll set up your workshop next."
                                    : 'Pick up where you left off.'}
                        </p>
                    </div>

                    {inviteOrgName && (
                        <div className="fda-invite">
                            <b>{inviteOrgName}</b>
                            {inviteRole && (
                                <span>
                                    Role: {inviteRole === 'admin' ? 'Accountant' : inviteRole.charAt(0).toUpperCase() + inviteRole.slice(1)}
                                </span>
                            )}
                        </div>
                    )}

                    <div className="fda-providers">
                        <button type="button" onClick={handleGoogleLogin} disabled={loading} className="fda-btn fda-btn--google">
                            <GoogleIcon />
                            Continue with Google
                        </button>

                        {/* Passkey. Sign-in only — you cannot register a passkey for
                            an account that does not exist yet, so this is hidden on
                            the signup tab rather than shown and then failing. */}
                        {passkeysSupported && !signup && (
                            <button type="button" onClick={handlePasskeyLogin} disabled={loading} className="fda-btn fda-btn--passkey">
                                <Fingerprint aria-hidden="true" />
                                Sign in with a passkey
                            </button>
                        )}
                    </div>
                    {notice && <p className="fda-msg fda-msg--info" role="status">{notice}</p>}

                    <p className="fda-or">or with your email</p>

                    <form onSubmit={handleAuth} className="fda-form">
                        {errorBox}
                        <div className="fda-field">
                            <label className="fda-label" htmlFor={emailId}>Email</label>
                            <input id={emailId} type="email" required readOnly={!!inviteEmail}
                                autoComplete={signup ? 'email' : 'username'} inputMode="email"
                                className="fda-input" placeholder="you@yourgarage.co.uk"
                                value={email}
                                aria-describedby={error ? errorId : undefined}
                                onChange={(e) => { if (!inviteEmail) setEmail(e.target.value); }} />
                        </div>

                        <div className="fda-field">
                            <div className="fda-field__top">
                                <label className="fda-label" htmlFor={passwordId}>Password</label>
                                {!signup && (
                                    <button type="button" className="fda-link fda-link--sm"
                                        onClick={() => { setView('forgot', { focusTitle: true }); setResetSent(false); }}>
                                        Forgot password?
                                    </button>
                                )}
                            </div>
                            <div className="fda-input-wrap">
                                <input id={passwordId} type={showPassword ? 'text' : 'password'} required
                                    minLength={signup ? MIN_PASSWORD_LENGTH : undefined}
                                    autoComplete={signup ? 'new-password' : 'current-password'}
                                    className="fda-input fda-input--pw"
                                    value={password}
                                    aria-describedby={[signup ? ruleId : '', error ? errorId : ''].filter(Boolean).join(' ') || undefined}
                                    onChange={(e) => setPassword(e.target.value)} />
                                <button type="button" className="fda-reveal"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    aria-pressed={showPassword} aria-controls={passwordId}
                                    onClick={() => setShowPassword(s => !s)}>
                                    {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                                </button>
                            </div>
                            {signup && (
                                <p id={ruleId} className="fda-rule" data-met={passwordRuleMet}>
                                    {passwordRuleMet ? <CheckCircle2 aria-hidden="true" /> : <Circle aria-hidden="true" />}
                                    <span>{describePasswordRule()}</span>
                                </p>
                            )}
                        </div>

                        {signup && (
                            <div className="fda-legal">
                                {!inviteOrgName && <p>ForgeDash currently supports garage businesses operated by sole traders in the UK. Tax figures are estimates; they do not file your return.</p>}
                                <label className="fda-check">
                                    <input type="checkbox" required checked={legalAccepted} onChange={e => setLegalAccepted(e.target.checked)} />
                                    <span>I accept the <a href="https://forgedash.uk/terms" target="_blank" rel="noopener noreferrer">Terms</a> and <a href="https://forgedash.uk/dpa" target="_blank" rel="noopener noreferrer">DPA</a>. See how we use your data in the <a href="https://forgedash.uk/privacy" target="_blank" rel="noopener noreferrer">Privacy Notice</a>.</span>
                                </label>
                            </div>
                        )}

                        <button type="submit" disabled={loading || !isFormValid} className="fda-btn fda-btn--primary">
                            {loading
                                ? <><Loader2 className="fda-spin" aria-hidden="true" /> Working…</>
                                : (inviteOrgName ? 'Join team' : signup ? 'Create account' : 'Sign in')}
                        </button>
                    </form>

                    {!inviteOrgName && (
                        <p className="fda-alt">
                            {signup ? 'Already have an account?' : 'New to ForgeDash?'}{' '}
                            <button type="button" className="fda-link"
                                onClick={() => setView(signup ? 'signin' : 'signup', { focusTitle: true })}>
                                {signup ? 'Sign in' : 'Create an account'}
                            </button>
                        </p>
                    )}
                </div>
            </>
        );
    }

    if (!dismissible) return <>{body}</>;

    // Overlay: a sheet over the page. Not used by the login screen today; kept
    // so the component still works where a dismissible form is wanted.
    return (
        <div data-theme="dark" className="fda fda-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="fda-sheet" role="dialog" aria-modal="true" aria-label={view === 'signup' ? 'Create account' : 'Sign in'}>
                <button type="button" className="fda-close" onClick={onClose} aria-label="Close">
                    <X className="w-5 h-5" aria-hidden="true" />
                </button>
                <div className="fda-sheet__inner">{body}</div>
            </div>
        </div>
    );
}
